import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { getMachines } from '../services/api';
import MachineModal from './MachineModal';
import TokenIcon from './TokenIcon';
import { useGacha } from '../features/gacha';
import { useAuth } from '../features/auth';
import Seo, { DEFAULT_DESCRIPTION, SITE_URL } from '../shared/Seo';
import { logPageView } from '../services/analytics';
import {
  createWelcomeMachine,
  WELCOME_MACHINE_ID,
} from '../constants/welcomeMachine';
import { DAILY_FREE_PLAY_LIMIT, getFreePlaysRemaining } from '../utils/freePlayUtils';

const MACHINE_STYLE = {
  brandColor: '#C12127',
  accent: '#21476B',
  backdrop: '#E9F1FA',
  hopperCapsules: [
    ['#F5F8FF', '#F0463D'], // PonPon coral
    ['#F5F8FF', '#2BA4B4'], // PonPon teal
    ['#F5F8FF', '#F6C34E'], // PonPon gold
    ['#F5F8FF', '#F0463D'], // PonPon coral
    ['#F5F8FF', '#2BA4B4'], // PonPon teal
    ['#F5F8FF', '#F6C34E'], // PonPon gold
  ],
  dropCapsule: ['#F5F8FF', '#2BA4B4'], // PonPon teal
  description:
    'Flagship assortments featuring evergreen mascots, Gundam micro kits, and seasonal character clips.',
};

const FALLBACK_IMAGE = '/assets/ponpon-fallback.svg';

// CHANGED: Carousel configuration - use 'featured' tag to control carousel display
const CAROUSEL_FEATURED_TAG = 'featured';
const HIDDEN_TAGS = ['featured']; // Tags that should not be displayed in the UI

const FALLBACK_PRIZE_POOL = [
  {
    id: 'fallback-prize-1',
    name: 'SD Gundam enamel pins',
    imageUrl: '',
    weight: 30,
    stock: 40,
    initialStock: 40,
  },
  {
    id: 'fallback-prize-2',
    name: 'Pokémon mascot straps',
    imageUrl: '',
    weight: 24,
    stock: 28,
    initialStock: 28,
  },
  {
    id: 'fallback-prize-3',
    name: 'Sailor Moon charms',
    imageUrl: '',
    weight: 18,
    stock: 20,
    initialStock: 20,
  },
  {
    id: 'fallback-prize-4',
    name: 'Mystery Capsule',
    imageUrl: '',
    weight: 12,
    stock: 12,
    initialStock: 12,
  },
];

const sanitizeString = (value, fallbackValue = '') =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : fallbackValue;

const normalizePrizeRecord = (record = {}, index = 0) => {
  const fallback = FALLBACK_PRIZE_POOL[index % FALLBACK_PRIZE_POOL.length];
  const fallbackId = fallback ? fallback.id : `prize-${index}`;
  const fallbackName = fallback ? fallback.name : `Prize ${index + 1}`;
  const fallbackImageUrl = fallback ? fallback.imageUrl : FALLBACK_IMAGE;
  const fallbackWeight = fallback && Number.isFinite(fallback.weight) ? fallback.weight : 1;
  const fallbackStock =
    fallback && Number.isFinite(fallback.stock) ? Math.trunc(fallback.stock) : 0;
  const fallbackInitialStock =
    fallback && Number.isFinite(fallback.initialStock)
      ? Math.trunc(fallback.initialStock)
      : fallbackStock;

  const id = sanitizeString(record.id, fallbackId);
  const name = sanitizeString(record.name, fallbackName);
  const imageUrl = sanitizeString(record.imageUrl, fallbackImageUrl);
  const weightNumber = Number(record.weight);
  const weight =
    Number.isFinite(weightNumber) && weightNumber > 0 ? weightNumber : fallbackWeight;
  const stockNumber = Number(record.stock);
  const stock =
    Number.isFinite(stockNumber) && stockNumber >= 0
      ? Math.trunc(stockNumber)
      : fallbackStock;
  const initialStockNumber = Number(record.initialStock);
  const initialStock =
    Number.isFinite(initialStockNumber) && initialStockNumber >= 0
      ? Math.trunc(initialStockNumber)
      : fallbackInitialStock ?? stock;

  const nextPrize = {
    id,
    name,
    imageUrl,
    weight,
    stock,
    initialStock,
  };

  if (record.rarity) {
    nextPrize.rarity = sanitizeString(record.rarity);
  }

  if (record.metadata && typeof record.metadata === 'object') {
    nextPrize.metadata = { ...record.metadata };
  }

  return nextPrize;
};

const computeInventoryFromPrizePool = (prizePool = []) =>
  prizePool.reduce(
    (totals, prize) => {
      totals.inventoryTotal += Number.isFinite(prize.initialStock) ? prize.initialStock : 0;
      totals.inventoryAvailable += Number.isFinite(prize.stock) ? prize.stock : 0;
      return totals;
    },
    { inventoryTotal: 0, inventoryAvailable: 0 }
  );

const createFallbackMachine = () => ({
  id: 'ponpon-fallback',
  name: 'PonPon! Capsule',
  brand: 'PonPon!',
  tagline: 'Capsule joy delivered everywhere.',
  tokenCost: 3,
  tags: ['PonPon!', 'PonPon!'],
  imageUrl: FALLBACK_IMAGE,
  prizePool: FALLBACK_PRIZE_POOL.map((item) => ({ ...item })),
  updatedAt: new Date().toISOString(),
});

const decorateMachineRecord = (record = {}, index = 0) => {
  const fallback = createFallbackMachine();
  const id = sanitizeString(record.id, `machine-${index}`);

  const rawName = record.name || record.displayName || fallback.name;
  const displayName =
    typeof rawName === 'string'
      ? rawName
      : rawName && typeof rawName === 'object'
      ? sanitizeString(rawName.name || rawName.label, fallback.name)
      : fallback.name;

  const displayImage =
    (rawName && typeof rawName === 'object' && sanitizeString(rawName.imageUrl || rawName.picture)) ||
    sanitizeString(record.imageUrl, fallback.imageUrl);

  const tokenCostNumber = Number(record.tokenCost);
  const tokenCost = Number.isFinite(tokenCostNumber) && tokenCostNumber >= 0 ? tokenCostNumber : fallback.tokenCost;

  const tagline = sanitizeString(record.tagline, '');

  const tags = Array.isArray(record.tags)
    ? record.tags
        .filter((tag) => typeof tag === 'string' && tag.trim().length > 0)
        .map((tag) => tag.trim())
    : [];

  const prizePoolSource =
    Array.isArray(record.prizePool) && record.prizePool.length ? record.prizePool : fallback.prizePool;
  const prizePool = prizePoolSource.map((prize, prizeIndex) => normalizePrizeRecord(prize, prizeIndex));

  const { inventoryAvailable, inventoryTotal } = computeInventoryFromPrizePool(prizePool);

  const metadata =
    record.metadata && typeof record.metadata === 'object'
      ? { ...record.metadata }
      : fallback.metadata || null;

  // A+: Optional SEO fields for future per-machine pages (data layer only)
  const seoTitle = sanitizeString(record.seoTitle || record.metaTitle, '') || null;
  const seoDescription = sanitizeString(record.seoDescription || record.metaDescription, '') || null;

  return {
    id,
    name: displayName,
    brand: sanitizeString(record.brand, fallback.brand),
    tagline: tagline || null,
    tokenCost,
    prizePool,
    tags,
    imageUrl: displayImage || FALLBACK_IMAGE,
    inventoryAvailable,
    inventoryTotal,
    metadata,
    updatedAt: record.updatedAt || fallback.updatedAt,
    seoTitle,
    seoDescription,
  };
};

const cloneMachineForState = (machine) => ({
  ...machine,
  prizePool: machine.prizePool.map((item) => ({ ...item })),
  tags: [...machine.tags],
  _isFeatured: machine._isFeatured ?? false, // CHANGED: Preserve featured flag
});

const FALLBACK_MACHINES = [decorateMachineRecord(createFallbackMachine(), 0)];
const FALLBACK_INITIAL_ID = FALLBACK_MACHINES[0]?.id ?? 'machine-0';

const Homepage = () => {
  const { latestMachineState } = useGacha();
  const { user } = useAuth();
  const freePlaysRemaining = getFreePlaysRemaining(user);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [machines, setMachines] = useState(() => FALLBACK_MACHINES.map((machine) => cloneMachineForState(machine)));
  const [selectedMachineId, setSelectedMachineId] = useState(FALLBACK_INITIAL_ID);
  const [isMachineModalOpen, setIsMachineModalOpen] = useState(false);
  const [isLoadingMachines, setIsLoadingMachines] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isPageTransitioning, setIsPageTransitioning] = useState(false);

  const appliedSpinRef = useRef(0);
  const pendingSpin = useRef(null);
  const cardRefs = useRef([]);
  const carouselSectionRef = useRef(null);
  const gallerySectionRef = useRef(null);
  const suppressScrollRef = useRef(false);

  // CHANGED: Keep welcome machine visible until user closes the modal
  const displayMachines = useMemo(() => {
    const showWelcome =
      (user && user.welcomeSpinUsed !== true) ||
      (selectedMachineId === WELCOME_MACHINE_ID && isMachineModalOpen);
    if (showWelcome && machines.length > 0) {
      const welcome = createWelcomeMachine();
      return [cloneMachineForState(welcome), ...machines];
    }
    return machines;
  }, [machines, user, selectedMachineId, isMachineModalOpen]);

  useEffect(() => {
    if (
      displayMachines.length > 0 &&
      displayMachines[0].id === WELCOME_MACHINE_ID &&
      (!selectedMachineId || !displayMachines.some((m) => m.id === selectedMachineId))
    ) {
      setSelectedMachineId(WELCOME_MACHINE_ID);
    }
  }, [displayMachines, selectedMachineId]);

  const selectedMachine = useMemo(() => {
    const machine = displayMachines.find((item) => item.id === selectedMachineId);
    return machine ?? displayMachines[0];
  }, [displayMachines, selectedMachineId]);

  const selectedMachineIndex = useMemo(() => {
    const index = displayMachines.findIndex((item) => item.id === selectedMachineId);
    return index >= 0 ? index : 0;
  }, [displayMachines, selectedMachineId]);

  // CHANGED: Carousel = welcome (if eligible) + featured machines
  const carouselMachines = useMemo(() => {
    return displayMachines.filter(
      (machine) => machine._isWelcome === true || machine._isFeatured === true,
    );
  }, [displayMachines]);

  const scrollToMachine = useCallback((index) => {
    const cardNode = cardRefs.current[index];
    if (cardNode && cardNode.scrollIntoView) {
      cardNode.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, []);

  useEffect(() => {
    if (selectedMachineIndex < 0) return;
    if (suppressScrollRef.current) {
      suppressScrollRef.current = false;
      return;
    }
    scrollToMachine(selectedMachineIndex);
  }, [selectedMachineIndex, scrollToMachine]);

  // Sync URL ?machine=id with modal: open modal when URL has machine; close when URL cleared (e.g. back)
  useEffect(() => {
    const machineId = searchParams.get('machine');
    if (machineId && displayMachines.some((m) => m.id === machineId)) {
      setSelectedMachineId(machineId);
      setIsMachineModalOpen(true);
    } else if (!machineId && isMachineModalOpen) {
      setIsMachineModalOpen(false);
    }
  }, [searchParams, displayMachines, isMachineModalOpen]);

  // CHANGED: Log machine page view to Google Analytics (Firebase) when URL has ?machine=id
  const lastTrackedMachineIdRef = useRef(null);
  useEffect(() => {
    const machineId = searchParams.get('machine');
    if (!machineId || !displayMachines.length) return;
    const machine = displayMachines.find((m) => m.id === machineId);
    if (!machine || lastTrackedMachineIdRef.current === machineId) return;
    lastTrackedMachineIdRef.current = machineId;
    const pageName = machine.seoTitle || machine.name || `Machine ${machineId}`;
    const pagePath = `/?machine=${machineId}`;
    logPageView(pageName, pagePath);
  }, [searchParams, displayMachines]);

  const handleSelectMachine = useCallback(
    (machineId, indexOverride = null, options = {}) => {
      suppressScrollRef.current = options.autoScroll === false;
      setSelectedMachineId(machineId);
      const targetIndex =
        indexOverride !== null ? indexOverride : displayMachines.findIndex((item) => item.id === machineId);
      if (targetIndex >= 0 && options.autoScroll !== false) {
        scrollToMachine(targetIndex);
      }
      if (options.openModal) {
        setIsMachineModalOpen(true);
        navigate({ pathname: '/', search: `?machine=${machineId}` }, { replace: false });
      }
    },
    [displayMachines, scrollToMachine, navigate]
  );

  // CHANGED: Find current machine index in carousel
  const selectedCarouselIndex = useMemo(() => {
    if (carouselMachines.length === 0) return -1;
    return carouselMachines.findIndex((machine) => machine.id === selectedMachineId);
  }, [carouselMachines, selectedMachineId]);

  // CHANGED: Navigate within carousel machines only
  const handlePrevMachine = useCallback(() => {
    if (carouselMachines.length === 0) return;
    const currentIndex = selectedCarouselIndex >= 0 ? selectedCarouselIndex : 0;
    const prevIndex = ((currentIndex - 1) % carouselMachines.length + carouselMachines.length) % carouselMachines.length;
    const prevMachine = carouselMachines[prevIndex];
    const originalIndex = displayMachines.findIndex((m) => m.id === prevMachine.id);
    if (originalIndex >= 0) {
      handleSelectMachine(prevMachine.id, originalIndex);
    }
  }, [carouselMachines, displayMachines, selectedCarouselIndex, handleSelectMachine]);

  const handleNextMachine = useCallback(() => {
    if (carouselMachines.length === 0) return;
    const currentIndex = selectedCarouselIndex >= 0 ? selectedCarouselIndex : 0;
    const nextIndex = (currentIndex + 1) % carouselMachines.length;
    const nextMachine = carouselMachines[nextIndex];
    const originalIndex = displayMachines.findIndex((m) => m.id === nextMachine.id);
    if (originalIndex >= 0) {
      handleSelectMachine(nextMachine.id, originalIndex);
    }
  }, [carouselMachines, displayMachines, selectedCarouselIndex, handleSelectMachine]);


  useEffect(() => {
    let isMounted = true;

    const hydrateMachines = async () => {
      setIsLoadingMachines(true);
      try {
        const records = await getMachines();
        if (!isMounted || !Array.isArray(records) || records.length === 0) {
          return;
        }
        // CHANGED: Mark featured machines and filter hidden tags
        const decorated = records.map((record, index) => {
          const machine = decorateMachineRecord(record, index);
          const rawTags = Array.isArray(record.tags)
            ? record.tags.map((tag) => String(tag).trim().toLowerCase())
            : [];
          const isFeatured = rawTags.includes(CAROUSEL_FEATURED_TAG.toLowerCase());
          // Filter out hidden tags from display
          const displayTags = machine.tags.filter(
            (tag) => !HIDDEN_TAGS.includes(tag.toLowerCase())
          );
          return {
            ...machine,
            tags: displayTags,
            _isFeatured: isFeatured,
          };
        });
        if (decorated.length === 0) {
          return;
        }
        setMachines(decorated.map((machine) => cloneMachineForState(machine)));
        // CHANGED: Select first featured machine if available, otherwise first machine
        const featuredMachines = decorated.filter((m) => m._isFeatured);
        const initialMachine = featuredMachines.length > 0 ? featuredMachines[0] : decorated[0];
        setSelectedMachineId((prev) =>
          decorated.some((machine) => machine.id === prev) ? prev : initialMachine.id
        );
      } catch (error) {
        if (process.env.NODE_ENV !== 'test') {
          console.error('Failed to load machines from API:', error);
        }
      } finally {
        if (isMounted) {
          setIsLoadingMachines(false);
        }
      }
    };

    hydrateMachines();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!latestMachineState) return;

    // CHANGED: Mark featured status and filter hidden tags when updating machine state
    const decorated = decorateMachineRecord(latestMachineState);
    const rawTags = Array.isArray(latestMachineState.tags)
      ? latestMachineState.tags.map((tag) => String(tag).trim().toLowerCase())
      : [];
    const isFeatured = rawTags.includes(CAROUSEL_FEATURED_TAG.toLowerCase());
    const displayTags = decorated.tags.filter(
      (tag) => !HIDDEN_TAGS.includes(tag.toLowerCase())
    );
    const updatedMachine = {
      ...decorated,
      tags: displayTags,
      _isFeatured: isFeatured,
    };

    setMachines((prev) => {
      const existingIndex = prev.findIndex((machine) => machine.id === updatedMachine.id);
      if (existingIndex === -1) {
        return [...prev, cloneMachineForState(updatedMachine)];
      }
      const next = [...prev];
      next[existingIndex] = cloneMachineForState(updatedMachine);
      return next;
    });

    if (
      pendingSpin.current &&
      pendingSpin.current.id === updatedMachine.id &&
      pendingSpin.current.sequence > appliedSpinRef.current
    ) {
      appliedSpinRef.current = pendingSpin.current.sequence;
      pendingSpin.current = null;
    }
  }, [latestMachineState, pendingSpin]);

  const MACHINES_PER_PAGE = 16;
  const totalPages = useMemo(() => Math.ceil(displayMachines.length / MACHINES_PER_PAGE), [displayMachines.length]);
  const gridMachines = useMemo(() => {
    const startIndex = (currentPage - 1) * MACHINES_PER_PAGE;
    const endIndex = startIndex + MACHINES_PER_PAGE;
    return displayMachines.slice(startIndex, endIndex);
  }, [displayMachines, currentPage]);

  const availableMachineCount = useMemo(
    () => displayMachines.filter((machine) => machine.inventoryAvailable > 0).length,
    [displayMachines],
  );

  // CHANGED: Reset to page 1 when machines list changes
  useEffect(() => {
    setCurrentPage(1);
  }, [displayMachines.length]);

  const handlePrevPage = useCallback(() => {
    setIsPageTransitioning(true);
    setTimeout(() => {
      setCurrentPage((prev) => Math.max(1, prev - 1));
      requestAnimationFrame(() => {
        setTimeout(() => setIsPageTransitioning(false), 10);
      });
    }, 200);
  }, []);

  const handleNextPage = useCallback(() => {
    setIsPageTransitioning(true);
    setTimeout(() => {
      setCurrentPage((prev) => Math.min(totalPages, prev + 1));
      requestAnimationFrame(() => {
        setTimeout(() => setIsPageTransitioning(false), 10);
      });
    }, 200);
  }, [totalPages]);

  // CHANGED: Scroll to gallery section when page changes
  useEffect(() => {
    if (gallerySectionRef.current && currentPage > 1) {
      gallerySectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentPage]);

  // CHANGED: auto-scroll to top on navigation (same pattern as support pages)
  useEffect(() => {
    if (location.pathname !== '/') {
      return;
    }

    const smoothScroll = () => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    const ensureTop = () => {
      const doc = document?.documentElement;
      const body = document?.body;
      const current = (doc?.scrollTop || 0) + (body?.scrollTop || 0);
      if (current > 0) {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    };

    smoothScroll();
    const retryId = window.setTimeout(ensureTop, 220);
    const fallbackId = window.setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, 900);

    return () => {
      window.clearTimeout(retryId);
      window.clearTimeout(fallbackId);
    };
  }, [location.pathname]);

  const heroStructuredData = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'PonPon! Featured Machines',
      description:
        'PonPon! delivers an online gachapon experience with real machines, Locker consolidation, and consolidated shipping.',
      url: 'https://ponpon.app/',
    }),
    []
  );

  // CHANGED: Show skeleton while loading, but still render the page structure
  const MachinesSkeleton = () => (
    <div className="machine-selector-panel rounded-[28px] border border-white/60 bg-white/70 p-4 shadow-lg backdrop-blur">
      <div className="machine-carousel">
        <div className="machine-carousel-viewport">
          <div className="machine-carousel-track">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="machine-carousel-slide">
                <div className="machine-tile animate-pulse">
                  <div className="machine-thumb">
                    <div className="machine-thumb-image bg-ponpon-navy/10 rounded-lg h-32 w-32" />
                  </div>
                  <div className="machine-summary">
                    <span className="machine-summary-brand bg-ponpon-navy/10 h-4 w-16 rounded" />
                    <p className="machine-name bg-ponpon-navy/10 h-6 w-24 rounded mt-2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const machineIdFromUrl = searchParams.get('machine');
  const machineForSeo =
    machineIdFromUrl && displayMachines.length > 0
      ? displayMachines.find((m) => m.id === machineIdFromUrl)
      : null;

  return (
    <div className="homepage flex w-full flex-col gap-16 pb-8">
      <Seo
        title={machineForSeo ? `PonPon! | ${machineForSeo.seoTitle || machineForSeo.name}` : undefined}
        description={
          machineForSeo
            ? machineForSeo.seoDescription || machineForSeo.tagline || DEFAULT_DESCRIPTION
            : DEFAULT_DESCRIPTION
        }
        canonical={machineForSeo ? `${SITE_URL}/?machine=${machineForSeo.id}` : `${SITE_URL}/`}
        image="/assets/Background.png"
        jsonLd={heroStructuredData}
      />
      <h1 className="sr-only">Online Gachapon – Spin Authentic Japanese Capsule Toys in the USA</h1>
      <section ref={carouselSectionRef} className="machine-selector">
        <div className="machine-selector-header">
          <div>
            <h2 className="machine-selector-title font-display text-3xl font-semibold text-ponpon-navy sm:text-4xl">
              Featured Gachapon Machine
            </h2>
          </div>
          <div className="machine-selector-buttons">
            <button
              type="button"
              onClick={handlePrevMachine}
              className="carousel-nav"
              aria-label="Previous machine"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={handleNextMachine}
              className="carousel-nav"
              aria-label="Next machine"
            >
              ›
            </button>
          </div>
        </div>

        {isLoadingMachines ? (
          <MachinesSkeleton />
        ) : (
          <div className="machine-selector-panel rounded-[28px] border border-white/60 bg-white/70 p-4 shadow-lg backdrop-blur">
            <div className="machine-carousel">
              <div className="machine-carousel-viewport">
                <div className="machine-carousel-track">
                  {carouselMachines.length > 0 ? (
                    carouselMachines.map((machine, carouselIndex) => {
                      // CHANGED: Index in displayMachines for refs and scroll (includes welcome machine)
                      const originalIndex = displayMachines.findIndex((m) => m.id === machine.id);
                      const displayIndex = originalIndex >= 0 ? originalIndex : carouselIndex;
                      const soldOut = machine.inventoryAvailable === 0;
                      const lowStock =
                        !soldOut &&
                        machine.inventoryAvailable <=
                          Math.max(5, Math.round(machine.inventoryTotal * 0.1));
                      const tileClass = [
                        'machine-tile',
                        soldOut ? 'sold-out' : '',
                        lowStock ? 'low-stock' : '',
                      ]
                        .filter(Boolean)
                        .join(' ');
                      return (
                        <div
                          className="machine-carousel-slide"
                          key={machine.id}
                          ref={(el) => {
                            cardRefs.current[displayIndex] = el;
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => handleSelectMachine(machine.id, displayIndex, { openModal: true })}
                            className={tileClass}
                            style={{
                              '--accent-color': MACHINE_STYLE.accent,
                            }}
                          >
                            <div className="machine-thumb">
                              {machine.imageUrl ? (
                                  <img
                                    src={machine.imageUrl}
                                    alt={machine.name}
                                    className="machine-thumb-image"
                                    loading={selectedMachineIndex === displayIndex ? 'eager' : 'lazy'}
                                    decoding="async"
                                  />
                              ) : (
                                <div className="machine-thumb-placeholder" aria-hidden="true">
                                  🎁
                                </div>
                              )}
                            </div>
                            <div className="machine-summary">
                              <span className="machine-summary-brand">{machine.brand}</span>
                              <p className="machine-name">{machine.name}</p>
                              <div className="machine-meta">
                                <span className={`machine-pill ${machine.tokenCost === 0 ? `machine-pill-free ${freePlaysRemaining === 0 ? 'is-exhausted' : ''}` : 'machine-pill-token'}`}>
                                  {machine.tokenCost === 0 ? (
                                    freePlaysRemaining === 0
                                      ? 'TOMORROW'
                                      : `Free Spin ${freePlaysRemaining}/${DAILY_FREE_PLAY_LIMIT}`
                                  ) : (
                                    <>
                                      <TokenIcon className="w-4 h-4" alt="tokens" />
                                      {machine.tokenCost} token{machine.tokenCost === 1 ? '' : 's'}
                                    </>
                                  )}
                                </span>
                                <span
                                  className={[
                                    'machine-pill machine-pill-stock',
                                    soldOut ? 'pill-muted' : lowStock ? 'pill-warn' : 'pill-stock',
                                  ].join(' ')}
                                >
                                  {soldOut ? 'Sold out' : `${machine.inventoryAvailable} left`}
                                </span>
                              </div>
                            </div>
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    // CHANGED: Show message if no featured machines
                    <div className="machine-carousel-slide" style={{ width: '100%', textAlign: 'center', padding: '2rem' }}>
                      <p className="text-ponpon-navy/60">No featured machines available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      <section ref={gallerySectionRef} className="machine-gallery">
        <div className="machine-grid-gallery-header">
          <div>
            <h3 className="machine-grid-gallery-title font-display text-3xl font-semibold text-ponpon-navy sm:text-4xl">
              Explore the Collection
            </h3>
          </div>
          <span className="machine-grid-gallery-count">
            {availableMachineCount} Machines Available
          </span>
        </div>

        <div className="machine-grid-gallery">
          <div className={`machine-grid-gallery-grid ${isPageTransitioning ? 'page-transitioning' : ''}`}>
            {gridMachines.map((machine) => {
              const soldOut = machine.inventoryAvailable === 0;
              const lowStock =
                !soldOut &&
                machine.inventoryAvailable <=
                  Math.max(5, Math.round(machine.inventoryTotal * 0.1));
              const tileClass = [
                'machine-tile',
                soldOut ? 'sold-out' : '',
                lowStock ? 'low-stock' : '',
                'machine-grid-tile',
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <button
                  key={machine.id}
                  type="button"
                  onClick={() =>
                    handleSelectMachine(machine.id, null, {openModal: true, autoScroll: false})
                  }
                  className={tileClass}
                  style={{
                    '--accent-color': MACHINE_STYLE.accent,
                  }}
                >
                  <div className="machine-thumb">
                    {machine.imageUrl ? (
                      <img
                        src={machine.imageUrl}
                        alt={machine.name}
                        className="machine-thumb-image"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="machine-thumb-placeholder" aria-hidden="true">
                        🎁
                      </div>
                    )}
                  </div>
                  <div className="machine-summary">
                    <span className="machine-summary-brand">{machine.brand}</span>
                    <p className="machine-name">{machine.name}</p>
                    <div className="machine-meta">
                      <span className={`machine-pill ${machine.tokenCost === 0 ? `machine-pill-free ${freePlaysRemaining === 0 ? 'is-exhausted' : ''}` : 'machine-pill-token'}`}>
                        {machine.tokenCost === 0 ? (
                          freePlaysRemaining === 0
                            ? 'TOMORROW'
                            : `Free Spin ${freePlaysRemaining}/${DAILY_FREE_PLAY_LIMIT}`
                        ) : (
                          <>
                            <TokenIcon className="w-4 h-4" alt="tokens" />
                            {machine.tokenCost} token{machine.tokenCost === 1 ? '' : 's'}
                          </>
                        )}
                      </span>
                      <span
                        className={[
                          'machine-pill machine-pill-stock',
                          soldOut ? 'pill-muted' : lowStock ? 'pill-warn' : 'pill-stock',
                        ].join(' ')}
                      >
                        {soldOut ? 'Sold out' : `${machine.inventoryAvailable} left`}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        {totalPages > 1 && (
          <div className="machine-grid-gallery-pagination">
            <button
              type="button"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="carousel-nav"
              aria-label="Previous page"
            >
              ‹
            </button>
            <span className="machine-grid-gallery-page-info">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="carousel-nav"
              aria-label="Next page"
            >
              ›
            </button>
          </div>
        )}
      </section>

      <MachineModal
        isOpen={isMachineModalOpen}
        machine={selectedMachine}
        onClose={() => {
          setIsMachineModalOpen(false);
          navigate({ pathname: '/', search: '' }, { replace: true });
        }}
      />

    </div>
  );
};

export default Homepage;
